import { useState } from 'react';

interface HeartButtonProps {
  isFavorited: boolean;
  favoritesCount: number;
  isLoading?: boolean;
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  className?: string;
}

export function HeartButton({
  isFavorited,
  favoritesCount,
  isLoading = false,
  onClick,
  size = 'md',
  showCount = true,
  className = '',
}: HeartButtonProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isLoading) return;

    // Trigger animation
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);

    onClick();
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const textSizeClasses = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const paddingClasses = {
    sm: 'p-1',
    md: 'p-1.5',
    lg: 'p-2',
  };

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`
        inline-flex items-center gap-1 rounded-full
        ${paddingClasses[size]}
        transition-all duration-200
        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}
        ${isFavorited ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}
        ${className}
      `}
      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
    >
      <svg
        viewBox="0 0 24 24"
        className={`
          ${sizeClasses[size]}
          ${isAnimating ? 'animate-ping-once' : ''}
          transition-transform duration-200
        `}
        fill={isFavorited ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
      {showCount && favoritesCount > 0 && (
        <span className={`${textSizeClasses[size]} font-medium min-w-[1rem] text-center`}>
          {favoritesCount > 999 ? '999+' : favoritesCount}
        </span>
      )}
    </button>
  );
}

// CSS for the ping animation (add to index.css or use Tailwind config)
// @keyframes ping-once {
//   0% { transform: scale(1); }
//   50% { transform: scale(1.3); }
//   100% { transform: scale(1); }
// }
// .animate-ping-once {
//   animation: ping-once 0.3s ease-in-out;
// }
